<template>
  <ol class="stepper" :aria-label="t('contestEntryForm.steps.ariaLabel')">
    <li
      v-for="step in steps"
      :key="step.id"
      class="step"
      :class="{ active: step.id === currentStep }"
    >
      <span class="step-badge" :class="{ active: step.id === currentStep }">{{ step.id }}</span>
      <span class="step-label">{{ step.label }}</span>
    </li>
  </ol>
</template>

<script setup lang="ts">
import { computed, defineProps } from "vue";
import { useI18n } from "vue-i18n-bridge";

defineProps<{
  currentStep: 1 | 2 | 3;
}>();

const { t } = useI18n();

const steps = computed(() => [
  { id: 1 as const, label: t("contestEntryForm.steps.applicantInfo") },
  { id: 2 as const, label: t("contestEntryForm.steps.selectWork") },
  { id: 3 as const, label: t("contestEntryForm.steps.confirm") },
]);
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.stepper {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.step {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 32px;
  padding: 4px 12px;
  border-bottom: 1px solid var(--color-border);
  background-color: var(--color-background-normal);
  color: var(--color-text-normal);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-default);

  &.active {
    border-bottom-color: var(--color-primary);
    font-weight: var(--font-weight-bold);
  }
}

.step-badge {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: var(--radius-circle);
  background-color: var(--color-surface-base);
  color: var(--color-text-normal);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-default);

  &.active {
    background-color: var(--color-text-normal);
    color: var(--color-text-invert);
  }
}

.step-label {
  min-width: 0;
}

@include mq("sp") {
  .stepper {
    flex-direction: row;
    width: 100%;
  }

  .step {
    flex: 1;
    min-width: 0;
    min-height: 32px;
    padding: 4px 12px;
    gap: 8px;
    font-size: var(--font-size-sm);
    line-height: var(--line-height-default);
    border-bottom: 1px solid var(--color-border);

    &.active {
      font-weight: var(--font-weight-bold);
      border-bottom-color: var(--color-primary);
    }
  }

  .step-badge {
    width: 24px;
    height: 24px;
  }

  .step-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
